// frontend/src/services/balanceBankService.js
import apiClient from './api';

const balanceBankService = {
  // ============================================================================
  // BALANCE BANK CONFIGURATION
  // ============================================================================

  /**
   * Get all balance banks
   */
  async getAllBanks(includeInactive = false) {
    const response = await apiClient.get('/admin/balance-banks', {
      params: { include_inactive: includeInactive }
    });
    return response.data;
  },

  /**
   * Get balance bank by ID
   */
  async getBankById(bankId) {
    const response = await apiClient.get(`/admin/balance-banks/${bankId}`);
    return response.data;
  },

  /**
   * Create new balance bank
   */
  async createBank(bankData) {
    const response = await apiClient.post('/admin/balance-banks', bankData);
    return response.data;
  },

  /**
   * Update balance bank
   */
  async updateBank(bankId, bankData) {
    const response = await apiClient.put(`/admin/balance-banks/${bankId}`, bankData);
    return response.data;
  },

  /**
   * Delete balance bank
   */
  async deleteBank(bankId) {
    const response = await apiClient.delete(`/admin/balance-banks/${bankId}`);
    return response.data;
  },

  /**
   * Get statistics
   */
  async getStatistics(year = null) {
    const response = await apiClient.get('/admin/balance-banks/statistics', {
      params: { year }
    });
    return response.data;
  },

  // ============================================================================
  // LEAVE TYPE MAPPING
  // ============================================================================

  /**
   * Get leave types in a bank
   */
  async getBankLeaveTypes(bankId) {
    const response = await apiClient.get(`/admin/balance-banks/${bankId}/leave-types`);
    return response.data;
  },

  /**
   * Assign leave type to bank
   */
  async assignLeaveType(bankId, leaveTypeId) {
    const response = await apiClient.post(`/admin/balance-banks/${bankId}/leave-types`, {
      leave_type_id: leaveTypeId
    });
    return response.data;
  },

  /**
   * Remove leave type from bank
   */
  async removeLeaveType(bankId, leaveTypeId) {
    const response = await apiClient.delete(
      `/admin/balance-banks/${bankId}/leave-types/${leaveTypeId}`
    );
    return response.data;
  },

  // ============================================================================
  // TENURE RULES
  // ============================================================================

  /**
   * Get tenure rules for a bank
   */
  async getTenureRules(bankId) {
    const response = await apiClient.get(`/admin/balance-banks/${bankId}/tenure-rules`);
    return response.data;
  },

  /**
   * Update tenure rules for a bank
   */
  async updateTenureRules(bankId, rules) {
    const response = await apiClient.put(`/admin/balance-banks/${bankId}/tenure-rules`, {
      rules
    });
    return response.data;
  },

  // ============================================================================
  // EMPLOYEE BALANCES
  // ============================================================================

  /**
   * Get all employee bank balances with filters
   */
  async getAllEmployeeBankBalances(filters = {}) {
    const response = await apiClient.get('/admin/balance-banks/employees', {
      params: filters
    });
    return response.data;
  },

  /**
   * Get employee bank balances
   */
  async getEmployeeBankBalances(employeeId, year = null) {
    const response = await apiClient.get(`/admin/balance-banks/employees/${employeeId}`, {
      params: { year }
    });
    return response.data;
  },

  /**
   * Get balance history for employee
   */
  async getBalanceHistory(employeeId, filters = {}) {
    const response = await apiClient.get(`/admin/balance-banks/employees/${employeeId}/history`, {
      params: filters
    });
    return response.data;
  },

  /**
   * Override employee balance
   */
  async overrideEmployeeBalance(employeeId, bankId, data) {
    const response = await apiClient.put(
      `/admin/balance-banks/employees/${employeeId}/${bankId}`,
      data
    );
    return response.data;
  },

  /**
   * Adjust used days for employee balance
   */
  async adjustUsedDays(employeeId, bankId, data) {
    const response = await apiClient.put(
      `/admin/balance-banks/employees/${employeeId}/${bankId}/adjust-used-days`,
      data
    );
    return response.data;
  },

  /**
   * Recalculate employee balance
   */
  async recalculateEmployeeBalance(employeeId, year = null) {
    const response = await apiClient.post(
      `/admin/balance-banks/employees/${employeeId}/recalculate`,
      {},
      { params: { year } }
    );
    return response.data;
  },

  /**
   * Bulk update employee balances
   */
  async bulkUpdateBalances(updates) {
    const response = await apiClient.post('/admin/balance-banks/bulk-update', {
      updates
    });
    return response.data;
  }
};

export default balanceBankService;
